import { BigNumber } from '@frontend/numeric';

import { EOrderType } from '@aqx-core/common/api/order';

// Interfaces for calculation parameters
export interface ICalculateEstimatedTPorSLParams {
  ask: number;
  bid: number;
  orderType: EOrderType;
  pointStep: number;
  contractSize: number;
  tpOrSlPoints: number;
  lotSize?: number;
  userPrice?: number;
}

export interface ICalculatePricePerLotSizeParams {
  pointStep: number;
  contractSize: number;
  lotSize?: number;
}

export interface ICalculateEstimatedProfitLossParams {
  lotSize: number;
  pointStep: number;
  tpOrSlPoints: number;
  contractSize: number;
}

export interface IPointsToPriceParams {
  input: number;
  points: number;
  orderType: EOrderType;
  pointStep: number;
}

export interface IPriceToPointsParams {
  input: number;
  price: number;
  pointStep: number;
  neutral?: boolean;
}

export interface ICalculateCopyToOrderParams {
  buy: number;
  sell: number;
  orderType: EOrderType;
  copiedStopLoss: number;
  copiedTakeProfit: number;
}

export interface IRetrieveMinimumStopLossOrTakeProfitParams {
  currentPrice: number;
  inputPrice: number;
  orderType: EOrderType;
  pointStep: number;
  stopsLevel: number;
}

export interface IStopLossAndTakeProfitPointsToPriceParams {
  price: number;
  orderType: EOrderType;
  pointStep: number;
  stopLoss?: number;
  takeProfit?: number;
}

// Helper function to check if order is open position
export const checkIsOpenPosition = (orderType?: EOrderType): boolean => {
  return orderType === EOrderType.BUY || orderType === EOrderType.SELL;
};

// Helper function to check if value is a valid number
export const checkIsNumber = (value: unknown): value is number => {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
};

// Helper function to convert BigNumber to number safely
export const bigNumberToNumber = (value: BigNumber): number | undefined => {
  if (!value || !value.isFinite()) {
    return undefined;
  }
  return value.toNumber();
};

// Helper function to get decimal places from a number
export const decimals = (value?: number): number => {
  if (!value || Math.floor(value) === value) {
    return 0;
  }
  return value.toString().split('.')[1]?.length || 0;
};

// Helper function to check if order type is a BUY order
export const isBuyOrder = (orderType: EOrderType): boolean => {
  return [EOrderType.BUY, EOrderType.BUY_STOP, EOrderType.BUY_LIMIT, EOrderType.BUY_STOP_LIMIT].includes(orderType);
};

// Helper function to check if order type is a SELL order
export const isSellOrder = (orderType: EOrderType): boolean => {
  return [EOrderType.SELL, EOrderType.SELL_STOP, EOrderType.SELL_LIMIT, EOrderType.SELL_STOP_LIMIT].includes(orderType);
};

// Calculate stop loss/take profit price limits for validation
export const calculateStopLossTakeProfitPriceLimit = (params: IRetrieveMinimumStopLossOrTakeProfitParams) => {
  const { currentPrice, inputPrice, orderType, pointStep, stopsLevel } = params;

  const isLimitOrder = !checkIsOpenPosition(orderType);

  const liveStopLossPriceLimit = stopLossPointsToPrice({
    input: currentPrice,
    points: stopsLevel,
    orderType,
    pointStep
  });

  const staticStopLossPriceLimit = stopLossPointsToPrice({
    input: inputPrice,
    points: stopsLevel,
    orderType,
    pointStep
  });

  const liveTakeProfitPriceLimit = takeProfitPointsToPrice({
    input: currentPrice,
    points: stopsLevel,
    orderType,
    pointStep
  });

  const staticTakeProfitPriceLimit = takeProfitPointsToPrice({
    input: inputPrice,
    points: stopsLevel,
    orderType,
    pointStep
  });

  if (isBuyOrder(orderType)) {
    return {
      stopLossRange: [0, isLimitOrder ? staticStopLossPriceLimit : liveStopLossPriceLimit],
      takeProfitRange: [isLimitOrder ? staticTakeProfitPriceLimit : liveTakeProfitPriceLimit]
    };
  }

  if (isSellOrder(orderType)) {
    return {
      stopLossRange: [isLimitOrder ? staticStopLossPriceLimit : liveStopLossPriceLimit],
      takeProfitRange: [0, isLimitOrder ? staticTakeProfitPriceLimit : liveTakeProfitPriceLimit]
    };
  }

  return {
    stopLossRange: [],
    takeProfitRange: []
  };
};

// Convert stop loss points to price
export const stopLossPointsToPrice = (params: IPointsToPriceParams): number | undefined => {
  const { input, points, orderType, pointStep } = params;

  const pricePerPoint = bigNumberToNumber(new BigNumber(points).multipliedBy(pointStep));

  if (pricePerPoint === undefined || (pricePerPoint && !checkIsNumber(pricePerPoint))) {
    return undefined;
  }

  if (isBuyOrder(orderType)) {
    return bigNumberToNumber(new BigNumber(input).minus(pricePerPoint));
  }

  if (isSellOrder(orderType)) {
    return bigNumberToNumber(new BigNumber(input).plus(pricePerPoint));
  }

  return undefined;
};

// Convert take profit points to price
export const takeProfitPointsToPrice = (params: IPointsToPriceParams): number | undefined => {
  const { input, points, orderType, pointStep } = params;

  const pricePerPoint = bigNumberToNumber(new BigNumber(points).multipliedBy(pointStep));

  if (pricePerPoint === undefined || (pricePerPoint && !checkIsNumber(pricePerPoint))) {
    return undefined;
  }

  if (isBuyOrder(orderType)) {
    return bigNumberToNumber(new BigNumber(input).plus(pricePerPoint));
  }

  if (isSellOrder(orderType)) {
    return bigNumberToNumber(new BigNumber(input).minus(pricePerPoint));
  }

  return undefined;
};

// Convert price difference to points
export const priceToPoints = (params: IPriceToPointsParams): number | undefined => {
  const { input, price, pointStep, neutral = false } = params;

  if (!checkIsNumber(input) || !checkIsNumber(price) || !checkIsNumber(pointStep) || pointStep === 0) {
    return undefined;
  }

  const difference = neutral ? Math.abs(input - price) : input - price;
  const points = difference / pointStep;

  return Math.round(points);
};

// Calculate estimated take profit or stop loss values
export const calculateEstimatedTakeProfitOrStopLoss = (params: ICalculateEstimatedTPorSLParams) => {
  const { ask, bid, contractSize, lotSize, pointStep, orderType, tpOrSlPoints, userPrice } = params;

  if (!lotSize) {
    return {
      estimatedStopLoss: undefined,
      estimatedTakeProfit: undefined
    };
  }

  const pricePerPoint = contractSize * pointStep * tpOrSlPoints;

  switch (orderType) {
    case EOrderType.BUY:
      return {
        estimatedStopLoss: bid - pricePerPoint,
        estimatedTakeProfit: bid + pricePerPoint
      };
    case EOrderType.SELL:
      return {
        estimatedStopLoss: ask + pricePerPoint,
        estimatedTakeProfit: ask - pricePerPoint
      };
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_LIMIT:
    case EOrderType.BUY_STOP_LIMIT:
    case EOrderType.SELL_STOP:
    case EOrderType.SELL_LIMIT:
    case EOrderType.SELL_STOP_LIMIT:
      return {
        estimatedStopLoss: userPrice ? userPrice - pricePerPoint : undefined,
        estimatedTakeProfit: userPrice ? userPrice + pricePerPoint : undefined
      };
    default:
      return {
        estimatedStopLoss: undefined,
        estimatedTakeProfit: undefined
      };
  }
};

// Calculate price per lot size
export const calculatePricePerLotSize = (params: ICalculatePricePerLotSizeParams): number | undefined => {
  const { lotSize, pointStep, contractSize } = params;

  if (!lotSize) {
    return undefined;
  }

  return bigNumberToNumber(new BigNumber(lotSize).times(contractSize).times(pointStep));
};

// Calculate estimated profit/loss
export const calculateEstimatedProfitLoss = (params: ICalculateEstimatedProfitLossParams): number | undefined => {
  const { lotSize, pointStep, contractSize, tpOrSlPoints } = params;

  return bigNumberToNumber(new BigNumber(lotSize).times(contractSize).times(pointStep).times(tpOrSlPoints));
};

// Calculate copy to order values
export const calculateCopyToOrder = (params: ICalculateCopyToOrderParams) => {
  const { buy, sell, orderType, copiedStopLoss, copiedTakeProfit } = params;

  switch (orderType) {
    case EOrderType.BUY:
      return {
        stopLoss: copiedStopLoss > sell ? undefined : copiedStopLoss,
        takeProfit: copiedTakeProfit >= sell ? copiedTakeProfit : undefined
      };
    case EOrderType.SELL:
      return {
        stopLoss: copiedStopLoss >= buy ? copiedStopLoss : undefined,
        takeProfit: copiedTakeProfit > buy ? undefined : copiedTakeProfit
      };
    default:
      break;
  }

  return {
    stopLoss: undefined,
    takeProfit: undefined
  };
};

// Convert both stop loss and take profit points to prices
export const stopLossAndTakeProfitPointsToPrice = (params: IStopLossAndTakeProfitPointsToPriceParams) => {
  const { price, stopLoss, takeProfit, orderType, pointStep } = params;

  let stopLossPrice = stopLoss;
  let takeProfitPrice = takeProfit;

  if (stopLoss) {
    stopLossPrice = stopLossPointsToPrice({
      input: price,
      points: stopLoss,
      orderType,
      pointStep
    });
  }

  if (takeProfit) {
    takeProfitPrice = takeProfitPointsToPrice({
      input: price,
      points: takeProfit,
      orderType,
      pointStep
    });
  }

  return {
    stopLossPrice,
    takeProfitPrice
  };
};
